import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { faqGroups } from '@/mocks/faqs';
import { useSeo, buildCanonical } from '@/hooks/useSeo';

const allFaqs = faqGroups.flatMap((group) => group.items);

export default function FaqPage() {
  const [openKey, setOpenKey] = useState<string | null>('about-0');

  useSeo({
    title: '常见问题｜看料避雷社 内容来源与使用方法',
    description:
      '看料避雷社常见问题解答：网站定位、内容来源、风险等级划分、避雷清单的用法、内容原则与反馈方式，一次说清你在使用前可能想了解的事。',
    keywords: '看料避雷社,常见问题,风险等级,内容原则,避雷清单用法',
    path: '/faq',
    image: 'https://readdy.ai/api/search-image?query=Warm%20editorial%20flat%20lay%20of%20question%20cards%20and%20a%20pen%20on%20a%20cream%20desk%2C%20amber%20and%20beige%20tones%2C%20minimal%20clean%20composition&width=1200&height=630&seq=og-kanliao-faq&orientation=landscape',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: allFaqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: buildCanonical('/') },
          { '@type': 'ListItem', position: 2, name: '常见问题', item: buildCanonical('/faq') },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <Breadcrumbs items={[{ name: '首页', to: '/' }, { name: '常见问题' }]} />

      <main className="flex-1">
        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100/60 border-b border-background-200">
          <span className="inline-flex items-center gap-2 text-xs font-label tracking-widest uppercase text-accent-600 mb-3">
            <span className="w-6 h-px bg-accent-400"></span>
            常见问题
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
            使用前，你可能想先了解这些
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed font-body max-w-3xl">
            关于内容来源、风险等级怎么划分、清单该怎么用，以及我们不做哪些事，这里集中回答一遍。
            如果还有没覆盖到的问题，欢迎邮件告诉我们。
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {faqGroups.map((group) => (
              <a
                key={group.id}
                href={`#${group.id}`}
                className="rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-sm font-label text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                {group.name}
              </a>
            ))}
          </div>
        </section>

        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              {faqGroups.map((group) => (
                <section key={group.id} id={group.id} className="mb-12 scroll-mt-24 last:mb-0">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-3">
                    <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                      <i className="ri-question-line"></i>
                    </span>
                    {group.name}
                  </h2>
                  <div className="flex flex-col divide-y divide-background-200 border-y border-background-200">
                    {group.items.map((item, index) => {
                      const key = `${group.id}-${index}`;
                      const isOpen = openKey === key;
                      return (
                        <div key={item.q}>
                          <button
                            type="button"
                            onClick={() => setOpenKey(isOpen ? null : key)}
                            className="w-full flex items-center justify-between gap-4 py-4 text-left cursor-pointer group"
                            aria-expanded={isOpen}
                          >
                            <span className="font-heading text-sm md:text-base font-bold text-foreground-950 group-hover:text-primary-700 transition-colors">
                              {item.q}
                            </span>
                            <span className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 transition-colors ${isOpen ? 'bg-primary-500 border-primary-500 text-background-50' : 'border-background-300 text-foreground-500'}`}>
                              <i className={isOpen ? 'ri-subtract-line' : 'ri-add-line'}></i>
                            </span>
                          </button>
                          {isOpen && (
                            <p className="pb-5 pr-12 text-sm text-foreground-600 leading-relaxed font-body">{item.a}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 flex flex-col gap-5">
                <div className="rounded-lg border border-background-200 bg-background-100/50 p-5">
                  <h3 className="font-heading text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-bookmark-3-line text-primary-600"></i>
                    快速跳转
                  </h3>
                  <ul className="space-y-2.5">
                    {faqGroups.map((group) => (
                      <li key={group.id}>
                        <a href={`#${group.id}`} className="text-sm font-body text-foreground-600 hover:text-primary-700 transition-colors">
                          {group.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
                  <h3 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-2">
                    <i className="ri-lightbulb-line text-secondary-600"></i>
                    没找到答案？
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 font-body">
                    先看看清单库，多数具体坑点都在里面；也可以直接邮件联系我们。
                  </p>
                  <Link
                    to="/guides"
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-label text-background-50 hover:bg-secondary-600 transition-colors whitespace-nowrap"
                  >
                    浏览避雷清单
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <a
                    href="mailto:contact@kanliaobilei.com"
                    rel="nofollow"
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-secondary-300 px-4 py-2.5 text-sm font-label text-secondary-800 hover:bg-secondary-100 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-mail-line"></i>
                    邮件反馈
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}